import React, { useState, useEffect } from "react";
import { Drawer, Form, Input, Button, Select, Row, Col, Typography } from "antd";
import AddressService from "../../services/AddressService";
import "./Company.scss";
import { filterOption, openNotification, handlePhonePaste, toTurkishUpper } from "../../utils";
import CompanyService from "../../services/CompanyService";
// OpenStreetMap (Leaflet)
// Not: react-leaflet ve leaflet paketlerini kurmanız gerekir:
// npm i react-leaflet leaflet
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Popconfirm } from 'antd';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface ControlAddressDrawerProps {
  visible: boolean;
  onClose: () => void;
  companyId: any;
  addresses: any;
  editAddress?: any;
}

const ControlAddressDrawer: React.FC<ControlAddressDrawerProps> = ({
  visible,
  onClose,
  companyId,
  addresses,
  editAddress,
}) => {
  const [form] = Form.useForm();
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [controlTitlePlaceholder, setControlTitlePlaceholder] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const cityIdsToShow = [2170, 2195, 2150, 2167, 2151, 2176, 2163, 2218, 2175];
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [leafletMap, setLeafletMap] = useState<any>(null);
  const DEFAULT_LAT = 41.0082; // İstanbul
  const DEFAULT_LNG = 28.9784; // İstanbul

  // Leaflet default marker icon fix (bundler path issue)
  const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  useEffect(() => {
    const getState = async () => {
      try {
        const response = await CompanyService.getStates(225);
        setCities(response.data);
      } catch (error) {
        console.error("Şehirler alınırken hata oluştu:", error);
      }
    };
    getState();
  }, []);

  // Drawer açıldığında Leaflet container boyutunu güncelle
  useEffect(() => {
    if (visible && leafletMap) {
      setTimeout(() => {
        try {
          leafletMap.invalidateSize();
        } catch { }
      }, 250);
    }
  }, [visible, leafletMap]);

  useEffect(() => {
    if (editAddress) {
      form.setFieldsValue({
        controlTitle: editAddress.title,
        controlAddress: editAddress.addressLine1,
        controlAddressLine2: editAddress.addressLine2,
        controlCity: editAddress.city,
        controlDistrict: editAddress.state,
        controlPostalCode: editAddress.postalCode,
        controlPhone: editAddress.phone,
        controlIdentityNumber: editAddress.identityNumber,
        controlTaxNumber: editAddress.taxNumber,
        controlCompanyName: editAddress.companyName,
        controlTaxOffice: editAddress.taxOffice,
        controlFullName: editAddress.fullName,
        controlSgkNumber: editAddress.sgkNumber,
      });

      // Lat/Lng
      if (editAddress.latitude != null && editAddress.longitude != null) {
        // Mevcut koordinatlar varsa formu/haritayı bunlarla başlat
        const latNum = Number(editAddress.latitude);
        const lngNum = Number(editAddress.longitude);
        setLat(latNum);
        setLng(lngNum);
        form.setFieldsValue({ controlLatitude: latNum, controlLongitude: lngNum });
      } else {
        // Düzenleme modunda koordinat yoksa varsayılan bir konum atanmasın
        setLat(null);
        setLng(null);
        form.setFieldsValue({ controlLatitude: undefined, controlLongitude: undefined });
      }

      const city = cities.find((c) => c.name === editAddress.city);
      if (city) {
        setSelectedCityId(city.id);
        getDistricts(city.id);
      }
    } else {
      const controlAddresses = addresses?.filter(
        (address: any) => address.type === "CONTROL"
      ) || [];

      let maxIndex = controlAddresses.length + 1;

      controlAddresses.forEach((address: any) => {
        const match = address.title?.match(/Kontrol Adresi (\d+)/);
        if (match) {
          const index = parseInt(match[1], 10);
          if (index >= maxIndex) {
            maxIndex = index + 1;
          }
        }
      });

      setControlTitlePlaceholder(`Kontrol Adresi ${maxIndex}`);
      form.resetFields();
      // Yeni adres eklerken varsayılan bir konum seçili gelmesin
      setLat(null);
      setLng(null);
      form.setFieldsValue({ controlLatitude: undefined, controlLongitude: undefined });
    }
  }, [editAddress, form, addresses]);

  const getDistricts = async (stateId: number) => {
    try {
      const response = await CompanyService.getDistricts(stateId);
      setDistricts(response.data);
    } catch (error) {
      console.error("İlçeler alınırken hata oluştu:", error);
    }
  };

  const handleCityChange = (value: number) => {
    const city = cities.find((c) => c.id === value);
    setSelectedCityId(value);
    form.setFieldsValue({ controlCity: city?.name, controlDistrict: undefined });
    setDistricts([]);
    if (value) {
      getDistricts(value);
    }
  };

  const handleDistrictChange = (value: string) => {
    form.setFieldsValue({ controlDistrict: value });
  };

  // Parse "https://maps.google.com/?q=41.070759,28.808908" style links or raw "lat,lng"
  const parseAndApplyLocation = (raw: string) => {
    if (!raw) return;
    let text = raw.trim();
    try {
      const url = new URL(text);
      const q = url.searchParams.get('q');
      if (q) text = q;
    } catch {
      // not a full URL, keep as is (maybe "lat,lng")
    }
    // Replace URL-encoded comma
    text = text.replace(/%2C/gi, ',');
    const m = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (m) {
      const latNum = Number(m[1]);
      const lngNum = Number(m[2]);
      if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
        setLat(latNum);
        setLng(lngNum);
        form.setFieldsValue({ controlLatitude: latNum, controlLongitude: lngNum });
      }
    }
  };

  const onFinish = async () => {
    try {
      setIsLoading(true);
      const values = await form.validateFields();
      const normalizedValues = {
        ...values,
        controlTitle: toTurkishUpper(values.controlTitle),
        controlAddress: toTurkishUpper(values.controlAddress),
        controlAddressLine2: toTurkishUpper(values.controlAddressLine2),
      };

      const newControlAddress = {
        addressLine1: normalizedValues.controlAddress || "",
        addressLine2: normalizedValues.controlAddressLine2 || "",
        city: normalizedValues.controlCity,
        phone: normalizedValues.controlPhone || "",
        state: normalizedValues.controlDistrict,
        postalCode: normalizedValues.controlPostalCode || "",
        country: "Turkey",
        latitude: normalizedValues.controlLatitude ?? lat ?? null,
        longitude: normalizedValues.controlLongitude ?? lng ?? null,
        type: "CONTROL",
        identityNumber: normalizedValues.controlIdentityNumber || "",
        taxNumber: normalizedValues.controlTaxNumber || "",
        companyName: normalizedValues.controlCompanyName || "",
        taxOffice: normalizedValues.controlTaxOffice || "",
        title: normalizedValues.controlTitle || controlTitlePlaceholder,
        fullName: normalizedValues.controlFullName || "",
        companyId: companyId,
        sgkNumber: normalizedValues.controlSgkNumber || null,
      };

      if (editAddress) {
        await AddressService.update(editAddress.id, newControlAddress);
        openNotification("success", "Başarılı", "Kontrol Adresi Güncellendi");
      } else {
        await AddressService.create(newControlAddress);
        openNotification("success", "Başarılı", "Kontrol Adresi Eklendi");
      }

      onClose();
      form.resetFields();
      setIsLoading(false);
    } catch (error) {
      console.error("Validation Failed:", error);
      openNotification(
        "error",
        "Hata",
        editAddress ? "Kontrol Adresi Güncellenemedi" : "Kontrol Adresi Eklenemedi"
      );
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      title={editAddress ? "Kontrol Adresi Düzenle" : "Yeni Kontrol Adresi Ekle"}
      width={720}
      onClose={onClose}
      visible={visible}
      afterVisibleChange={(open) => {
        if (open && leafletMap) {
          // Drawer animasyonu tamamlandıktan sonra boyutu güncelle
          setTimeout(() => {
            try {
              leafletMap.invalidateSize();
            } catch { }
          }, 250);
        }
      }}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div className="text-right">
          <Button onClick={onFinish} type="primary" loading={isLoading} className="d-button dark-button width-auto">
            Kaydet
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="controlTitle"
          label="Adres Başlığı"
          rules={[{ required: true, message: "Adres Başlığı zorunludur" }]}
        >
          <Input
            placeholder="Merkez, Üretim, Depo, Şube gibi"
            className="d-input d-input-form"
            lang="tr"
            style={{ textTransform: "uppercase" }}
          />
        </Form.Item>
        <Form.Item
          name="controlAddress"
          label="Adres"
        >
          <Input.TextArea
            placeholder="Adres"
            className="d-input d-input-form"
            lang="tr"
            style={{ textTransform: "uppercase" }}
          />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="controlCity"
              label="İl"
              rules={[{ required: true, message: "İl seçimi zorunludur" }]}
            >
              <Select
                placeholder="İl seçin"
                className="d-input d-input-form"
                showSearch
                filterOption={filterOption}
                onChange={handleCityChange}
              >
                {cities
                  .filter((city) => cityIdsToShow.includes(city.id))
                  .map((city) => (
                    <Select.Option key={city.id} value={city.id}>
                      {city.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="controlDistrict"
              label="İlçe"
              rules={[{ required: true, message: "İlçe seçimi zorunludur" }]}
            >
              <Select
                placeholder="İlçe seçin"
                className="d-input d-input-form"
                showSearch
                filterOption={filterOption}
                onChange={handleDistrictChange}
                disabled={!selectedCityId}
              >
                {districts.map((district) => (
                  <Select.Option key={district.id} value={district.name}>
                    {district.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="controlPhone"
              label="Telefon Numarası"
              rules={[
                { pattern: /^[1-9]/, message: 'Telefon Numarası 0 ile başlamamalıdır' },
                { pattern: /^\d{10}$/, message: 'Telefon Numarası toplamda 10 haneli olmalıdır' },
              ]}
            >
              <Input
                addonBefore="+90"
                placeholder="Telefon Numarası"
                className="d-input d-input-form"
                maxLength={10}
                onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'controlPhone' })}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="controlSgkNumber"
              label="SGK/DETSİS Numarası"
              rules={[
                { pattern: /^[0-9]*$/, message: 'SGK/DETSİS Numarası sadece rakamlardan oluşmalıdır' },
                { min: 8, message: 'SGK/DETSİS Numarası en az 8 haneli olmalıdır' },
                { max: 26, message: 'SGK/DETSİS Numarası en fazla 26 haneli olmalıdır' }
              ]}
            >
              <Input
                placeholder="SGK/DETSİS Numarası"
                maxLength={26}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="controlLocationLink" label="Konum Linki (Google Maps)">
              <Input
                placeholder="https://maps.google.com/?q=41.070759,28.808908 veya 41.070759,28.808908"
                className="d-input d-input-form"
                onChange={(e) => parseAndApplyLocation(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Konum Seçimi (OpenStreetMap) */}
        <Row gutter={16}>
          <Col span={24}>
            <Typography.Text>Konum (Harita Üzerinden Seçiniz)</Typography.Text>
            <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', marginTop: 8, position: 'relative' }}>
              {/* Sil (koordinatları temizle) - yalnızca lat/lng doluysa göster */}
              {(lat !== null && lng !== null) && (
                <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}>
                  <Popconfirm
                    title="Emin misiniz?"
                    okText="Evet"
                    cancelText="Hayır"
                    onConfirm={() => {
                      setLat(null);
                      setLng(null);
                      form.setFieldsValue({ controlLatitude: null, controlLongitude: null });
                    }}
                  >
                    <Button danger size='small'>Seçimi kaldır</Button>
                  </Popconfirm>
                </div>
              )}
              <MapContainer
                center={lat && lng ? [lat, lng] as any : [DEFAULT_LAT, DEFAULT_LNG]}
                zoom={lat && lng ? 12 : 8}
                style={{ height: '100%', width: '100%' }}
                whenCreated={(map: any) => setLeafletMap(map)}
              >
                {/* Ensure proper sizing when Drawer opens */}
                <InvalidateSizeOnShow active={visible} />
                {/* Center map on coord changes (edit or paste link) */}
                <FlyToPosition lat={lat} lng={lng} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {/* Marker */}
                {lat && lng && (
                  <Marker position={[lat, lng] as any} icon={defaultIcon} />
                )}
                <MapClick
                  onClick={(e: { lat: number; lng: number }) => {
                    setLat(e.lat);
                    setLng(e.lng);
                    form.setFieldsValue({ controlLatitude: e.lat, controlLongitude: e.lng });
                  }}
                />
              </MapContainer>
            </div>
            {lat && lng && (
              <div style={{ marginTop: 8 }}>
                <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noreferrer">
                  Google Maps'te aç
                </a>
              </div>
            )}
          </Col>
        </Row>
        {/** Enlem/Boylam alanlarını formda göstermiyoruz; değerler haritadan seçiliyor ve payload'a ekleniyor. **/}

      </Form>
    </Drawer>
  );
};

export default ControlAddressDrawer;

// Haritaya tıklamayı yakalamak için küçük yardımcı bileşen
function MapClick({ onClick }: { onClick: (e: { lat: number; lng: number }) => void }) {
  useMapEvents({
    // LeafletMouseEvent type is available via @types/leaflet
    click(e: any) {
      onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

// Koordinatlar değiştiğinde haritayı konuma uçur
function FlyToPosition({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng] as any, Math.max(map.getZoom(), 12));
    }
  }, [lat, lng, map]);
  return null;
}

// Drawer görünür olduğunda Leaflet boyutunu birden fazla kez güncelle
function InvalidateSizeOnShow({ active }: { active: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return;
    const invalidate = () => {
      try { map.invalidateSize(); } catch { }
    };
    // Birkaç kez çağırarak transition/paint gecikmelerini yakala
    invalidate();
    const t1 = setTimeout(invalidate, 100);
    const t2 = setTimeout(invalidate, 300);
    const t3 = setTimeout(invalidate, 600);
    return () => {
      clearTimeout(t1 as any);
      clearTimeout(t2 as any);
      clearTimeout(t3 as any);
    };
  }, [active, map]);
  return null;
}
